import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Booking {
  id: string;
  listing_id: string;
  renter_id: string;
  host_id: string;
  start_date: string;
  end_date: string;
  status: string;
  total_amount: number;
  host_payout?: number;
  platform_fee?: number;
  payment_link?: string;
  payment_status?: string;
  monthly_rate?: number;
  months?: number;
  expires_at?: string;
  created_at: string;
  cancellation_notice_end?: string;
  stripe_subscription_id?: string;
  listing?: {
    id: string;
    title: string;
    images: string[];
  };
  renter?: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
}

type RoleTab = 'all' | 'renting' | 'hosting';
type StatusTab = 'all' | 'upcoming' | 'active' | 'completed';

export default function BookingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roleTab, setRoleTab] = useState<RoleTab>('all');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [renterBookings, setRenterBookings] = useState<Booking[]>([]);
  const [hostBookings, setHostBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingBooking, setCancellingBooking] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccessMsg, setCancelSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch bookings where user is the renter
        const { data: renterData, error: renterError } = await supabase
          .from('bookings')
          .select(`
            *,
            listings:listing_id (
              id,
              title,
              price_per_month,
              images,
              host_id
            )
          `)
          .eq('renter_id', user.id)
          .order('created_at', { ascending: false });

        if (renterError) throw renterError;

        // Fetch bookings where user is the host
        const { data: hostData, error: hostError } = await supabase
          .from('bookings')
          .select(`
            *,
            listings:listing_id (
              id,
              title,
              price_per_month,
              images,
              host_id
            )
          `)
          .eq('host_id', user.id)
          .order('created_at', { ascending: false });

        if (hostError) throw hostError;

        // Fetch renter profiles for host bookings
        const renterIds = [...new Set((hostData || []).map(b => b.renter_id))];
        let renterProfiles: Record<string, { full_name: string; avatar_url: string; email: string; phone: string }> = {};
        
        if (renterIds.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, full_name, avatar_url, email, phone')
            .in('id', renterIds);
          
          if (profilesData) {
            renterProfiles = profilesData.reduce((acc, p) => {
              acc[p.id] = { full_name: p.full_name || 'Guest', avatar_url: p.avatar_url || '', email: p.email || '', phone: p.phone || '' };
              return acc;
            }, {} as Record<string, { full_name: string; avatar_url: string }>);
          }
        }

        // Fetch reviews by this user to check which bookings have reviews
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select('booking_id')
          .eq('reviewer_id', user.id);

        if (reviewsError) throw reviewsError;

        // Create a set of booking IDs that have reviews
        const reviewedBookingIds = new Set(reviewsData?.map((r) => r.booking_id) || []);

        // Add has_review flag to renter bookings
        const renterBookingsWithReviewStatus = (renterData || []).map((booking) => ({
          ...booking,
          has_review: reviewedBookingIds.has(booking.id),
        }));

        // Add renter profile to host bookings
        const hostBookingsWithProfiles = (hostData || []).map((booking) => ({
          ...booking,
          renter_profile: renterProfiles[booking.renter_id] || null,
        }));

        setRenterBookings(renterBookingsWithReviewStatus);
        setHostBookings(hostBookingsWithProfiles);
      } catch (_error) {
        // silently handle fetch error
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user?.id]);

  const handleCancelBooking = async (bookingId: string) => {
    if (!user) return;
    setCancelLoading(true);

    try {
      const booking = [...renterBookings, ...hostBookings].find(b => b.id === bookingId);
      if (!booking) throw new Error('Booking not found');

      const response = await fetch('https://sibpoabenkejwltenabf.supabase.co/functions/v1/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          bookingId: booking.id,
          cancelledBy: booking.renter_id === user.id ? 'renter' : 'host'
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err.error || 'Failed to cancel subscription');
      }

      setCancelModalOpen(false);
      setCancellingBooking(null);
      setCancelSuccessMsg('Cancellation notice sent. Your booking will end in 30 days.');
      setTimeout(() => setCancelSuccessMsg(null), 5000);

      // Refresh bookings list
      const { data: renterData } = await supabase
        .from('bookings')
        .select(`*, listings:listing_id (id, title, price_per_month, images, host_id)`)
        .eq('renter_id', user.id)
        .order('created_at', { ascending: false });
      const { data: hostData } = await supabase
        .from('bookings')
        .select(`*, listings:listing_id (id, title, price_per_month, images, host_id)`)
        .eq('host_id', user.id)
        .order('created_at', { ascending: false });
      setRenterBookings(renterData || []);
      setHostBookings(hostData || []);

    } catch (error) {
      alert('Failed to send cancellation notice. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleMessageUser = async (booking: Booking, isHost: boolean) => {
    if (!user) {
      navigate('/login');
      return;
    }

    const otherUserId = isHost ? booking.renter_id : booking.listings?.host_id;

    try {
      // Check if conversation already exists
      const { data: existingConvo } = await supabase
        .from('conversations')
        .select('id')
        .or(
          `and(participant1_id.eq.${user.id},participant2_id.eq.${otherUserId}),and(participant1_id.eq.${otherUserId},participant2_id.eq.${user.id})`
        )
        .maybeSingle();

      if (existingConvo) {
        navigate(`/messages/${existingConvo.id}`);
        return;
      }

      // Create new conversation
      const { data: newConvo, error } = await supabase
        .from('conversations')
        .insert({
          participant1_id: user.id,
          participant2_id: otherUserId,
          listing_id: booking.listing_id,
        })
        .select()
        .single();

      if (error) throw error;

      navigate(`/messages/${newConvo.id}`);
    } catch (_error) {
      // silently handle messaging error
    }
  };

  const handleAcceptBooking = async (bookingId: string) => {
    if (!confirm('Accept this booking request? The renter will be notified to complete payment.')) return;
    const { data: booking } = await supabase.from('bookings').select('*').eq('id', bookingId).single();
    if (!booking) return;

    let paymentLink: string | null = null;

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          listingId: booking.listing_id,
          renterId: booking.renter_id,
          startDate: booking.start_date,
          endDate: booking.end_date,
          months: booking.months,
          successUrl: `${window.location.origin}/bookings?success=true`,
          cancelUrl: `${window.location.origin}/bookings`
        })
      });
      const session = await response.json();
      paymentLink = session?.url || null;
      console.log('✅ Payment link created:', paymentLink);
    } catch (err) {
      console.error('❌ Failed to create checkout session:', err);
    }

    await supabase.from('bookings').update({ status: 'payment_pending', payment_link: paymentLink }).eq('id', bookingId);

    const { data: convo } = await supabase.from('conversations').select('id')
      .or(`participant1_id.eq.${user?.id},participant2_id.eq.${user?.id}`)
      .or(`participant1_id.eq.${booking.renter_id},participant2_id.eq.${booking.renter_id}`)
      .maybeSingle();
    let conversationId = convo?.id;
    if (!conversationId) {
      const { data: newConvo } = await supabase.from('conversations').insert({ participant1_id: user?.id, participant2_id: booking.renter_id }).select('id').single();
      conversationId = newConvo?.id;
    }
    if (conversationId) {
      await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: paymentLink
          ? `Your booking request has been accepted! 🎉\n\nPlease click the link below to complete your payment:\n\n${paymentLink}`
          : `Your booking request has been accepted! Please go to your Bookings dashboard to complete payment.`,
        read: false
      });
    }

    setRenterBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'payment_pending', payment_link: paymentLink } : b));
    setHostBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'payment_pending', payment_link: paymentLink } : b));

    // Send email to renter
    try {
      const { data: renterProfile } = await supabase.from('profiles').select('email, full_name').eq('id', booking.renter_id).single();
      const { data: listingData } = await supabase.from('listings').select('title').eq('id', booking.listing_id).single();
      if (renterProfile?.email) {
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-accepted-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            renterEmail: renterProfile.email,
            renterName: renterProfile.full_name || 'Renter',
            listingTitle: listingData?.title || 'your listing',
            paymentLink,
            startDate: booking.start_date,
            endDate: booking.end_date,
            months: booking.months
          })
        });
      }
    } catch (emailErr) {
      console.warn('Renter acceptance email failed:', emailErr);
    }
  };

  const handleDeclineBooking = async (bookingId: string, renterId: string) => {
    if (!confirm('Decline this booking request? The renter will be notified.')) return;
    await supabase.from('bookings').update({ status: 'declined' }).eq('id', bookingId);
    const { data: convo } = await supabase.from('conversations').select('id').or(`participant1_id.eq.${user?.id},participant2_id.eq.${user?.id}`).or(`participant1_id.eq.${renterId},participant2_id.eq.${renterId}`).maybeSingle();
    let conversationId = convo?.id;
    if (!conversationId) {
      const { data: newConvo } = await supabase.from('conversations').insert({ participant1_id: user?.id, participant2_id: renterId }).select('id').single();
      conversationId = newConvo?.id;
    }
    if (conversationId) {
      await supabase.from('messages').insert({ conversation_id: conversationId, sender_id: user?.id, content: `Unfortunately your booking request has been declined by the host. Please browse other available spaces on Stashly.`, read: false });
    }
    setRenterBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'declined' } : b));
    setHostBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'declined' } : b));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-[#6366F1] animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading bookings...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const allBookings = [...renterBookings, ...hostBookings];
  const totalBookings = allBookings.length;

  // Show empty state if no bookings at all
  if (!loading && totalBookings === 0) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-96 pt-32">
          <div className="text-center">
            <i className="ri-calendar-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings yet</h3>
            <p className="text-gray-600 mb-6">Book a storage space or list your own to see bookings here</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/browse')}
                className="px-6 py-3 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0F9C8E] transition-colors cursor-pointer whitespace-nowrap"
              >
                Browse Listings
              </button>
              <button
                onClick={() => navigate('/list-space')}
                className="px-6 py-3 bg-[#6366F1] text-white rounded-lg hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap"
              >
                List Your Space
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Get bookings based on role tab
  const getBookingsByRole = () => {
    if (roleTab === 'renting') return renterBookings;
    if (roleTab === 'hosting') return hostBookings;
    return allBookings;
  };

  // Filter by status
  const filteredBookings = getBookingsByRole().filter((booking) => {
    if (statusTab === 'all') return true;
    if (statusTab === 'active') return booking.status === 'active' || booking.status === 'confirmed';
    return booking.status === statusTab;
  });

  // Sort by created_at descending
  const sortedBookings = [...filteredBookings].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending_host_approval: 'bg-amber-100 text-amber-800',
      payment_pending: 'bg-blue-100 text-blue-800',
      declined: 'bg-red-100 text-red-800',
      cancelling: 'bg-orange-100 text-orange-800',
      upcoming: 'bg-purple-100 text-purple-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-teal-100 text-teal-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      upcoming: 'ri-calendar-event-line',
      active: 'ri-time-line',
      completed: 'ri-checkbox-circle-line',
      cancelled: 'ri-close-circle-line',
      pending: 'ri-hourglass-line',
      confirmed: 'ri-check-double-line',
    };
    return icons[status] || 'ri-question-line';
  };

  // Check if booking is eligible for review (completed or active status)
  const canLeaveReview = (booking: Booking) => {
    return booking.status === 'completed' || booking.status === 'active';
  };

  // Check if user is the renter for this booking
  const isRenterBooking = (booking: Booking) => {
    return booking.renter_id === user?.id;
  };

  const isRenter = (booking: Booking) => {
    return booking.renter_id === user?.id;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#6366F1] mb-4 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="font-medium">Back</span>
            </button>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Bookings</h1>
                <p className="text-gray-600 mt-2">View and manage all your storage rentals</p>
              </div>
              <button
                onClick={() => navigate('/browse')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#6366F1] text-white font-semibold rounded-lg hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line"></i>
                <span>Browse Spaces</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Total Bookings</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-900">{totalBookings}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-check-line text-blue-600 text-xl md:text-2xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">As Renter</p>
                  <p className="text-2xl md:text-3xl font-bold text-[#14B8A6]">{renterBookings.length}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-[#14B8A6] text-xl md:text-2xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">As Host</p>
                  <p className="text-2xl md:text-3xl font-bold text-[#6366F1]">{hostBookings.length}</p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <i className="ri-home-heart-line text-[#6366F1] text-xl md:text-2xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">Active</p>
                  <p className="text-2xl md:text-3xl font-bold text-green-600">
                    {allBookings.filter((b) => b.status === 'active' || b.status === 'confirmed').length}
                  </p>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-green-600 text-xl md:text-2xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Role Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
            <div className="flex gap-2 p-2">
              {([
                { key: 'all', label: 'All Bookings', icon: 'ri-list-check' },
                { key: 'renting', label: 'Spaces I\'m Renting', icon: 'ri-shopping-bag-line' },
                { key: 'hosting', label: 'Spaces I\'m Hosting', icon: 'ri-home-heart-line' },
              ] as const).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setRoleTab(tab.key)}
                  className={`flex-1 px-3 md:px-4 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base ${
                    roleTab === tab.key
                      ? 'bg-[#6366F1] text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} mr-1 md:mr-2`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">
                    {tab.key === 'all' ? 'All' : tab.key === 'renting' ? 'Renting' : 'Hosting'}
                  </span>
                  <span className="ml-1 md:ml-2 text-xs md:text-sm">
                    ({tab.key === 'all' ? totalBookings : tab.key === 'renting' ? renterBookings.length : hostBookings.length})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter Tabs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="flex gap-1 p-2 overflow-x-auto">
              {(['all', 'upcoming', 'active', 'completed'] as const).map((tab) => {
                const currentBookings = getBookingsByRole();
                const count = tab === 'all' 
                  ? currentBookings.length 
                  : tab === 'active'
                  ? currentBookings.filter((b) => b.status === 'active' || b.status === 'confirmed').length
                  : currentBookings.filter((b) => b.status === tab).length;
                return (
                  <button
                    key={tab}
                    onClick={() => setStatusTab(tab)}
                    className={`flex-1 px-2 md:px-4 py-2 md:py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap text-xs md:text-sm ${
                      statusTab === tab
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    <span className="ml-1 md:ml-2 text-xs">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bookings Grid */}
          {sortedBookings.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sortedBookings.map((booking) => {
                const isRenter = isRenterBooking(booking);
                return (
                  <div key={booking.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Role Badge */}
                    <div className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 ${
                      isRenter ? 'bg-teal-50 text-[#14B8A6]' : 'bg-indigo-50 text-[#6366F1]'
                    }`}>
                      <i className={isRenter ? 'ri-shopping-bag-line' : 'ri-home-heart-line'}></i>
                      {isRenter ? 'You\'re Renting' : 'You\'re Hosting'}
                    </div>

                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="md:w-48 h-48 md:h-auto flex-shrink-0">
                        <img
                          src={
                            booking.listings?.images?.[0] ||
                            'https://readdy.ai/api/search-image?query=empty%20storage%20space%20clean%20minimal%20interior&width=400&height=300&seq=placeholder&orientation=landscape'
                          }
                          alt={booking.listings?.title || 'Storage Space'}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-lg font-bold text-gray-900">{booking.listings?.title || 'Storage Space'}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusBadge(booking.status)}`}>
                            <i className={getStatusIcon(booking.status)}></i>
                            {({'pending_host_approval': 'Awaiting Approval', 'payment_pending': 'Payment Pending', 'active': 'Active', 'confirmed': 'Confirmed', 'completed': 'Completed', 'cancelled': 'Cancelled', 'cancelling': 'Cancelling', 'declined': 'Declined', 'pending': 'Pending'} as Record<string,string>)[booking.status] || booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </span>
                        </div>

                        {/* Show renter info for host bookings */}
                        {!isRenter && booking.renter_profile && (
                          <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                              {booking.renter_profile.avatar_url ? (
                                <img src={booking.renter_profile.avatar_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <i className="ri-user-line text-gray-500"></i>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Renter</p>
                              <p className="text-sm font-medium text-gray-900">{booking.renter_profile.full_name}</p>
                              {(booking.status === 'confirmed' || booking.status === 'active') && booking.renter_profile.email && (
                                <p className="text-xs text-gray-500">{booking.renter_profile.email}</p>
                              )}
                              {(booking.status === 'confirmed' || booking.status === 'active') && booking.renter_profile.phone && (
                                <p className="text-xs text-gray-500">{booking.renter_profile.phone}</p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-price-tag-3-line"></i>
                            <span>${booking.listings?.price_per_month || 0}/month</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <i className="ri-calendar-line"></i>
                            <span>
                              {new Date(booking.start_date).toLocaleDateString('en-AU', {
                                day: 'numeric',
                                month: 'short',
                              })}{' '}
                              -{' '}
                              {new Date(booking.end_date).toLocaleDateString('en-AU', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          {booking.cancellation_notice_end && (
                            <div className="flex items-center gap-2 text-sm text-orange-600 font-medium">
                              <i className="ri-alarm-warning-line"></i>
                              <span>Cancellation notice: ends {new Date(booking.cancellation_notice_end).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                          )}
                        </div>

                        <div className="pt-4 border-t border-gray-200">
                          {(isRenter || ['active', 'confirmed', 'completed'].includes(booking.status)) && (
                            <div className="mb-3">
                              <p className="text-sm text-gray-600">{isRenter ? (booking.status === 'payment_pending' ? 'Amount Due' : 'Total Paid') : 'Your Earnings'}</p>
                              <p className="text-2xl font-bold text-[#6366F1]">${(booking.renter_id === user?.id ? booking.total_amount : (booking.host_payout || booking.total_amount)).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleMessageUser(booking, !isRenter)}
                              className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                            >
                              <i className="ri-message-3-line mr-1"></i>
                              Message
                            </button>
                            <button
                              onClick={() => navigate(`/listing/${booking.listing_id}`)}
                              className="px-4 py-2 bg-[#6366F1] text-white font-semibold rounded-lg hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap text-sm"
                            >
                              <i className="ri-eye-line mr-1"></i>
                              View
                            </button>
                          </div>
                        </div>

                        {/* Leave Review Button for Renter Bookings Only */}
                        {isRenter && canLeaveReview(booking) &&
                          (booking.has_review ? (
                            <div className="mt-4 text-center py-2.5 bg-green-50 text-green-700 rounded-lg font-medium">
                              <i className="ri-check-line mr-2"></i>
                              Review Submitted
                            </div>
                          ) : (
                            <button
                              onClick={() => navigate(`/bookings/${booking.id}/review`)}
                              className="mt-4 w-full px-4 py-2.5 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0F9C8E] font-medium transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-star-line mr-2"></i>
                              Leave Review
                            </button>
                          ))}

                        {!isRenter && booking.status === 'pending_host_approval' && (
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => handleAcceptBooking(booking.id)}
                              className="flex-1 px-4 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors text-sm cursor-pointer"
                            >
                              ✓ Accept Request
                            </button>
                            <button
                              onClick={() => handleDeclineBooking(booking.id, booking.renter_id)}
                              className="flex-1 px-4 py-2 bg-red-100 text-red-700 font-semibold rounded-lg hover:bg-red-200 transition-colors text-sm cursor-pointer"
                            >
                              ✗ Decline
                            </button>
                          </div>
                        )}
                        {isRenter && booking.status === 'payment_pending' && (
                          <div className="mt-3">
                            <a
                              href={booking.payment_link}
                              className="block w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-sm text-center cursor-pointer"
                            >
                              💳 Complete Payment
                            </a>
                          </div>
                        )}
                        {isRenter && booking.status === 'pending_host_approval' && (
                          <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-700 font-semibold">⏳ Awaiting host approval</p>
                            <p className="text-xs text-amber-600 mt-1">The host has 48 hours to respond.</p>
                          </div>
                        )}
                        {isRenter && (booking.status === 'active' || booking.status === 'confirmed') && !booking.cancellation_notice_end && (
                          <button
                            onClick={() => {
                              setCancellingBooking(booking.id);
                              setCancelModalOpen(true);
                            }}
                            className="mt-3 w-full px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 font-medium transition-colors cursor-pointer"
                          >
                            <i className="ri-close-circle-line mr-2"></i>
                            Cancel Booking
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No bookings found</h3>
              <p className="text-gray-600 mb-6">
                {roleTab === 'renting' 
                  ? "You haven't rented any spaces yet" 
                  : roleTab === 'hosting'
                  ? "No one has booked your spaces yet"
                  : "You don't have any bookings yet"}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => navigate('/browse')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#14B8A6] text-white font-semibold rounded-lg hover:bg-[#0F9C8E] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line"></i>
                  <span>Browse Spaces</span>
                </button>
                {roleTab === 'hosting' && (
                  <button
                    onClick={() => navigate('/list-space')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#6366F1] text-white font-semibold rounded-lg hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    <span>List Your Space</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {cancelSuccessMsg && (
        <div className="fixed top-24 right-4 z-50 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg shadow-lg max-w-sm">
          <div className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-green-600"></i>
            <span className="text-sm font-medium">{cancelSuccessMsg}</span>
          </div>
        </div>
      )}

      {cancelModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Cancel Booking</h3>
            {(() => {
              const b = [...renterBookings, ...hostBookings].find(bk => bk.id === cancellingBooking);
              if (!b) return null;
              const endDate = new Date();
              endDate.setDate(endDate.getDate() + 30);
              return (
                <p className="text-gray-600 text-sm mb-6">
                  Are you sure? This will give 30 days notice to both you and the {b.renter_id === user?.id ? 'host' : 'renter'}. The booking will end on {endDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              );
            })()}
            <div className="flex gap-3">
              <button
                onClick={() => { setCancelModalOpen(false); setCancellingBooking(null); }}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
              >
                Keep Booking
              </button>
              <button
                onClick={() => handleCancelBooking(cancellingBooking!)}
                disabled={cancelLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {cancelLoading ? 'Sending Notice...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
